import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Layout from "../../components/layout/Layout";
import LeaveTypeView from "./components/LeaveTypeView";
import { leaveApi } from "../../api/leaveApi";
import { useCanModify } from "../../hooks/usePermissions";

const LeaveTypeTable = () => {
  // View-only roles see the list without create / edit / delete
  const canModify = useCanModify("leave", { company: true });
  const [showModal, setShowModal] = useState(false);
  const [selectedLeaveType, setSelectedLeaveType] = useState(null); // Added state
  const queryClient = useQueryClient();

  // Fetching data
  const { data: leaveTypes = [], isLoading } = useQuery({
    queryKey: ["leaveTypes"],
    queryFn: leaveApi.getLeaveTypes,
  });

  // Mutation for deleting
  const deleteMutation = useMutation({
    mutationFn: leaveApi.deleteLeaveType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leaveTypes"] });
      toast.success("Leave type deleted successfully!");
    },
  });

  // Function to handle Edit click
  const handleEditClick = (leaveType) => {
    setSelectedLeaveType(leaveType); // Pass the data to state
    setShowModal(true); // Open the modal
  };

  // Function to handle Add click
  const handleAddNew = () => {
    setSelectedLeaveType(null); // Clear state for new item
    setShowModal(true);
  };

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4 position-relative">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <h1 className="fw-bold text-dark m-0 page-title">Leave Types</h1>
            <p className="text-muted small mb-0">
              Define and manage company leave policies
            </p>
          </div>
          {canModify && (
            <button
              className="btn btn-sm px-3 text-white btn-navy h-35px d-flex align-items-center"
              onClick={handleAddNew} // Updated to use helper function }
            >
              <i className="bi bi-plus-circle me-2"></i>Add New Leave Type
            </button>
          )}
        </div>

        {/* Table Section */}
        <div className="glass-table-card">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="custom-table-header">
                <tr>
                  <th className="py-3 ps-4 border-0">LEAVE NAME</th>
                  <th className="py-3 border-0">DESCRIPTION</th>
                  <th className="py-3 border-0">CREATED AT</th>
                  <th className="py-3 border-0">UPDATED AT</th>
                  <th className="py-3 pe-4 border-0 text-end">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="5" className="text-center py-4">
                      Loading...
                    </td>
                  </tr>
                ) : (
                  leaveTypes.map((lt) => (
                    <tr key={lt.id} className="align-middle">
                      <td className="ps-4 fw-bold table-cell-text">
                        {lt.name}
                      </td>
                      <td className="table-cell-text">{lt.description}</td>
                      <td className="table-cell-text">
                        {new Date(lt.createdAt).toLocaleDateString()}
                      </td>
                      <td className="table-cell-text">
                        {new Date(lt.updatedAt).toLocaleDateString()}
                      </td>
                      <td className="text-end pe-4">
                        {canModify ? (
                          <>
                            <button
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => handleEditClick(lt)}
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => deleteMutation.mutate(lt.id)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </>
                        ) : (
                          <span className="fs-070 text-slate-400">
                            View only
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal: Pass the selectedLeaveType so the modal knows what to edit */}
        {showModal && (
          <LeaveTypeView
            onClose={() => setShowModal(false)}
            leaveType={selectedLeaveType}
          />
        )}
      </div>
    </Layout>
  );
};

export default LeaveTypeTable;
